// Copyright 2016 Sorama B.V.

import { autoinject, computedFrom } from "aurelia-framework";
import { Router } from "aurelia-router";
import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { HelpWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/HelpWorkflowModuleCatalog";
import { IUrlStateService } from "libraries/WorkflowFramework/Interfaces/IUrlStateService";
import { IWorkflowModuleConfig } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { IWorkflowModuleController, IWorkflowRouter } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import { IWorkflowSettings } from "libraries/WorkflowFramework/Interfaces/IWorkflowSettings";
import { SidebarRouter } from "libraries/WorkflowFramework/Sidebar/SidebarRouter";
import { WorkflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyObserverManager";

@autoinject
export class ShellViewManager {
    //#region Private Properties
    private _rightSideBarModule: IWorkflowModuleController;
    //#endregion

    //#region Element Visibility Properties
    public isNavBarVisible = true;
    public isPageContentVisible = true;

    private static navigationCompletedPromise: (value?: void | PromiseLike<void>) => void;
    private static navigationCompletedRejectedPromise: (value?: void | PromiseLike<void>) => void;

    @computedFrom("_rightSideBarModule")
    public get rightSidebarPanel(): IWorkflowModuleController {
        return this._rightSideBarModule;
    }

    @computedFrom("_rightSideBarModule")
    public get rightSidebarPanelName(): string {
        return this._rightSideBarModule ? this._rightSideBarModule.config.name : "";
    }

    public get activeWorkflow(): IWorkflowRouter {
        const viewport = this.router.currentInstruction.viewPortInstructions["default"];
        const viewModel = viewport.component.viewModel;

        if (!viewModel) {
            return undefined;
        }

        // Please DO NOT reference WorkflowRouter. This will trigger a circular dependency, because of reasons..
        if ((<IWorkflowSettings>viewModel).workflowName != undefined) {
            return viewModel;
        }

        return undefined;
    }

    //#endregion

    //#region Constructor and Aurelia Callbacks
    constructor(
        private readonly workflowCatalog: IWorkflowModuleCatalog,
        private readonly urlStateService: IUrlStateService,
        private readonly router: Router
    ) {}

    //#endregion
    public openRightSideBarHelp(articleName: string): Promise<void> {
        return this.openRightSideBar(HelpWorkflowModuleCatalog.HelpModule, [
            {
                prefix: SidebarRouter.prefix,
                name: "topicName",
                value: articleName,
            },
        ]);
    }

    public openRightSideBar(
        workflowModule: IWorkflowModuleConfig | string,
        properties: WorkflowProperty[] = []
    ): Promise<void> {
        const config: IWorkflowModuleConfig =
            typeof workflowModule == "string"
                ? this.workflowCatalog.getWorkflowModules().find((x) => x.name == workflowModule)
                : <IWorkflowModuleConfig>workflowModule;

        if (!config) {
            this.urlStateService.deletePropertyStates(SidebarRouter.prefix);
        } else {
            this.urlStateService.updatePropertyState([
                { name: "name", value: config.name, prefix: SidebarRouter.prefix },
                ...properties.map((x) => {
                    x.prefix = SidebarRouter.prefix;
                    return x;
                }),
            ]);
        }

        if (
            (this.rightSidebarPanel == undefined && workflowModule == undefined) ||
            (this.rightSidebarPanel && config && this.rightSidebarPanel.config.name == config.name)
        ) {
            // Already visible
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            ShellViewManager.navigationCompletedPromise = resolve;
            ShellViewManager.navigationCompletedRejectedPromise = reject;
        });
    }

    public closeRightSideBar(): Promise<void> {
        return this.openRightSideBar(undefined);
    }

    public static sidebarNavigationComplete() {
        if (this.navigationCompletedPromise) {
            this.navigationCompletedPromise();
        } else if (this.navigationCompletedRejectedPromise) {
            this.navigationCompletedRejectedPromise();
        }

        this.navigationCompletedPromise = undefined;
        this.navigationCompletedRejectedPromise = undefined;
    }
}
